import {
  EligibleExpense,
  FundingSource,
  InvestmentActivity,
  ProgramFilterStatusBackEndKey,
  SubsidyTypeCode,
} from 'utils/enums';

/**
 * @description Converts raw API response data into a format compatible state management in React.
 * Use this to populate React values after fetching data from the backend.
 * @param {object} apiData The API response object.
 * @returns {object} State-compatible data object.
 */
export function mapApiDataToState(apiData) {
  if (!apiData) return {};

  const stateData = {};
  return stateData;
}

/**
 * @description
 * Maps the internal React filter state (from useFundingFilters)
 * to the backend API payload expected by the funding search endpoint.
 *
 * @param {object} state - The current filters state.
 * @returns {object} - Normalized API payload.
 */
export function mapStateDataToApi(state) {
  if (!state) return {};

  const {
    openProgram,
    closeToExpire,
    expired,
    // fundingInsights,
    dateFrom,
    dateTo,
    subsidy,
    espa,
    taa,
    selectedRegionHQ,
    selectedRegionUnity,
    selectedMunicipality,
  } = state;

  // Collect region codes
  const regionCodes = [
    ...(selectedRegionHQ || []),
    ...(selectedRegionUnity || []),
    ...(selectedMunicipality || []),
  ].map(regionCode => regionCode.code);

  // Funding Source
  let fundingSourceCode;
  switch (true) {
    case espa === true: {
      fundingSourceCode = FundingSource.ESPA.backendKey;
      break;
    }
    case taa === true: {
      fundingSourceCode = FundingSource.RESILIENCE.backendKey;
      break;
    }
    default: {
      fundingSourceCode = null;
      break;
    }
  }

  // Get all active expense, subsidy, and activity codes dynamically
  const expenseClassCodes = extractActiveBackendKeys(state, EligibleExpense);
  const subsidyTypeCodes = extractActiveBackendKeys(state, SubsidyTypeCode);
  const kadCodes = extractActiveBackendKeys(state, InvestmentActivity);

  const payload = {
    [ProgramFilterStatusBackEndKey.Open]: !!openProgram,
    [ProgramFilterStatusBackEndKey.ClosingSoon]: !!closeToExpire,
    [ProgramFilterStatusBackEndKey.Expired]: !!expired,
    // [ProgramFilterStatusBackEndKey.Insights]: !!fundingInsights,
    gtInsightsPrograms: false, // Should change on wave 2
    startOfSubmission: formatDateToApi(dateFrom),
    endOfSubmissionTo: formatDateToApi(dateTo),
    minFundingPercentage:
      subsidy?.[0] === 0 && subsidy?.[1] === 100 ? null : subsidy?.[0] ?? null,
    maxFundingPercentage:
      subsidy?.[0] === 0 && subsidy?.[1] === 100 ? null : subsidy?.[1] ?? null,
    minFundingAmount: findFundingAmount(state)[0],
    maxFundingAmount: findFundingAmount(state)[1],
    fundingSourceCode,
    expenseClassCodes: expenseClassCodes.length ? expenseClassCodes : undefined,
    subsidyTypeCodes: subsidyTypeCodes.length ? subsidyTypeCodes : undefined,
    kadCodes: kadCodes.length ? kadCodes : undefined,
    regionCodes: regionCodes.length ? regionCodes : undefined,
  };

  // Remove false or null or undefined or empty lists(arrays) fields from request payload
  let apiData = Object.fromEntries(
    Object.entries(payload).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        !(Array.isArray(value) && value.length === 0)
    )
  );

  /**
   * @summary Send boolean to BackEnd for Statuses
   */
  if (
    Object.values(ProgramFilterStatusBackEndKey).every(item =>
      Object.entries(apiData ?? {}).some(
        ([key, value]) => item === key && value === false
      )
    )
  ) {
    apiData[ProgramFilterStatusBackEndKey.ClosingSoon] = true;
    apiData[ProgramFilterStatusBackEndKey.Expired] = true;
    apiData[ProgramFilterStatusBackEndKey.Open] = true;
    // apiData[ProgramFilterStatusBackEndKey.Insights] = true;
  }

  return apiData;
}

export function mapStateToHasFilters(state) {
  if (!state) return false;

  const {
    openProgram,
    closeToExpire,
    expired,
    // fundingInsights,
    dateFrom,
    dateTo,
    subsidy,
    espa,
    taa,
    selectedRegionHQ,
    selectedRegionUnity,
    selectedMunicipality,
  } = state;

  // Collect region codes
  const regionCodes = [
    ...(selectedRegionHQ || []),
    ...(selectedRegionUnity || []),
    ...(selectedMunicipality || []),
  ].map(regionCode => regionCode.code);

  // Funding Source
  let fundingSourceCode;
  switch (true) {
    case espa === true: {
      fundingSourceCode = FundingSource.ESPA.backendKey;
      break;
    }
    case taa === true: {
      fundingSourceCode = FundingSource.RESILIENCE.backendKey;
      break;
    }
    default: {
      fundingSourceCode = null;
      break;
    }
  }

  // Get all active expense, subsidy, and activity codes dynamically
  const expenseClassCodes = extractActiveBackendKeys(state, EligibleExpense);
  const subsidyTypeCodes = extractActiveBackendKeys(state, SubsidyTypeCode);
  const kadCodes = extractActiveBackendKeys(state, InvestmentActivity);

  const payload = {
    [ProgramFilterStatusBackEndKey.Open]: !!openProgram,
    [ProgramFilterStatusBackEndKey.ClosingSoon]: !!closeToExpire,
    [ProgramFilterStatusBackEndKey.Expired]: !!expired,
    // [ProgramFilterStatusBackEndKey.Insights]: !!fundingInsights,
    gtInsightsPrograms: false, // Should change on wave 2
    startOfSubmission: formatDateToApi(dateFrom),
    endOfSubmissionTo: formatDateToApi(dateTo),
    minFundingPercentage:
      subsidy?.[0] === 0 && subsidy?.[1] === 100 ? null : subsidy?.[0] ?? null,
    maxFundingPercentage:
      subsidy?.[0] === 0 && subsidy?.[1] === 100 ? null : subsidy?.[1] ?? null,
    minFundingAmount: findFundingAmount(state)[0],
    maxFundingAmount: findFundingAmount(state)[1],
    fundingSourceCode,
    expenseClassCodes: expenseClassCodes.length ? expenseClassCodes : undefined,
    subsidyTypeCodes: subsidyTypeCodes.length ? subsidyTypeCodes : undefined,
    kadCodes: kadCodes.length ? kadCodes : undefined,
    regionCodes: regionCodes.length ? regionCodes : undefined,
  };

  // Remove false or null or undefined or empty lists(arrays) fields from request payload
  return (
    Object.entries(payload).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== false &&
        !(Array.isArray(value) && value.length === 0)
    ).length > 0
  );
}

/**
 * @description Extracts all backend keys whose `state[value]` is true.
 */
function extractActiveBackendKeys(state, enumObj) {
  return Object.values(enumObj)
    .filter(item => state[item.value] === true)
    .map(item => item.backendKey);
}

/**
 * @description Formats a Date, Day.js, or string to `YYYY-MM-DD`.
 */
function formatDateToApi(date) {
  if (!date) return null;
  try {
    const d =
      typeof date === 'string' ? new Date(date) : date.toDate?.() ?? date;
    return d.toISOString().split('T')[0];
  } catch {
    return null;
  }
}

function findFundingAmount(state) {
  const { maxSubsidy, sliderFinancing, subsidyCheck1 } = state;

  let minNotLoan = null;
  let maxNotLoan = null;
  let minLoan = null;
  let maxLoan = null;

  // NOT LOAN
  if (maxSubsidy?.[0] === 0 && maxSubsidy?.[1] === 1000000) {
    minNotLoan = null;
    maxNotLoan = null;
  } else {
    minNotLoan = maxSubsidy?.[0] ?? null;
    maxNotLoan = maxSubsidy?.[1] ?? null;
  }
  if (subsidyCheck1 === false) {
    // LOAN
    if (sliderFinancing?.[0] === 0 && sliderFinancing?.[1] === 1000000) {
      minLoan = null;
      maxLoan = null;
    } else {
      minLoan = sliderFinancing?.[0] ?? null;
      maxLoan = sliderFinancing?.[1] ?? null;
    }
  }

  /**
   * *** Loan and has value on financing ***
   */
  if (subsidyCheck1 === false && (minLoan !== null || maxLoan !== null)) {
    return [minLoan, maxLoan];
  }
  return [minNotLoan, maxNotLoan];
}
